import {
  type LeaveCalendarHoliday,
  type LeaveCalendarYearAbsence,
  type LeaveCalendarYearTotal,
} from '@gb-ticket/shared-types';
import { Link } from 'react-router';
import { useMemo } from 'react';

import { cn } from '@/lib/utils';
import {
  leavePanelClassName,
  leaveTableFrameClassName,
} from '@/products/leave/features/leave-panel';
import { LeaveDayCell } from '@/products/leave/features/leave-day-cell';
import {
  buildYearRows,
  formatGermanDate,
  monthLabel,
  monthName,
  schoolHolidayNameOn,
  type SchoolHolidaySpan,
} from '@/products/leave/features/leave-calendar-utils';
import { formatOvertimeHours } from '@/products/leave/features/leave-home';
import { LEAVE_REQUEST_STATUS_LABEL } from '@/products/leave/features/leave-labels';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import { leaveRequestDetailPath } from '@/products/leave/leave-paths';

export function LeaveYearGrid({
  year,
  absences,
  holidays,
  schoolHolidays = [],
  today,
  linkRequests,
  rangeStart = null,
  onPickDate,
  className,
}: {
  year: number;
  absences: readonly LeaveCalendarYearAbsence[];
  holidays: readonly LeaveCalendarHoliday[];
  schoolHolidays?: readonly SchoolHolidaySpan[];
  today: string;
  linkRequests: boolean;
  rangeStart?: string | null;
  onPickDate?: (date: string) => void;
  className?: string;
}) {
  const rows = useMemo(() => buildYearRows(year), [year]);
  const holidayByDate = useMemo(() => new Map(holidays.map((row) => [row.date, row])), [holidays]);
  const absencesByDate = useMemo(() => groupAbsencesByDate(absences), [absences]);

  return (
    <div className={cn(leaveTableFrameClassName, 'p-3', className)}>
      <table className="w-max border-separate border-spacing-0 text-sm">
        <tbody>
          {rows.map((row) => (
            <tr key={row.month} id={`leave-year-month-${row.month}`}>
              <th
                scope="row"
                className="sticky left-0 z-10 bg-card px-2 py-0.5 text-left text-xs font-semibold text-foreground capitalize"
              >
                {monthName(row.month)}
              </th>
              {row.days.map((day) => {
                const dayAbsences = absencesByDate.get(day.date) ?? [];
                const holiday = holidayByDate.get(day.date);
                const schoolName = schoolHolidayNameOn(day.date, schoolHolidays);
                const primary = dayAbsences[0] ?? null;
                const second = dayAbsences[1] ?? null;
                const status =
                  primary?.status === 'approved' || primary?.status === 'submitted'
                    ? primary.status
                    : null;
                const href =
                  linkRequests && primary ? leaveRequestDetailPath(primary.requestId) : null;
                return (
                  <td key={day.date} className="p-0.5">
                    <LeaveDayCell
                      size="day"
                      dayNumber={day.day}
                      color={primary?.color}
                      mark={primary?.mark ?? null}
                      extraMark={second?.mark ?? null}
                      moreMarks={dayAbsences.length > 2}
                      status={status}
                      part={primary?.part ?? 'full'}
                      holiday={Boolean(holiday)}
                      holidayName={holiday?.nameDe ?? null}
                      schoolHoliday={schoolName != null}
                      schoolHolidayName={schoolName}
                      weekend={day.weekday === 0 || day.weekday === 6}
                      today={day.date === today}
                      labelDe={primary?.labelDe ?? null}
                      href={href}
                      selected={rangeStart === day.date}
                      onSelect={href || !onPickDate ? undefined : () => onPickDate(day.date)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LeaveYearTotals({
  totals,
  className,
}: {
  totals: readonly LeaveCalendarYearTotal[];
  className?: string;
}) {
  if (totals.length === 0) {
    return (
      <p
        className={cn(
          leavePanelClassName,
          'text-sm text-muted-foreground lg:text-right',
          className,
        )}
      >
        In diesem Jahr keine Abwesenheit.
      </p>
    );
  }

  return (
    <ul
      className={cn(
        leavePanelClassName,
        'flex flex-col gap-1.5 text-sm lg:items-end lg:text-right',
        className,
      )}
    >
      {totals.map((row) => {
        const key = `${row.absenceTypeId ?? 'overtime'}|${row.status}|${row.unit}`;
        const statusWord =
          row.status === 'approved' || row.status === 'submitted'
            ? LEAVE_REQUEST_STATUS_LABEL[row.status]
            : null;
        const amount =
          row.unit === 'hours' ? formatOvertimeHours(row.amount) : formatLeaveDays(row.amount);
        const unitLabel = row.unit === 'hours' ? null : 'Tage';
        return (
          <li key={key} className="flex flex-wrap items-center gap-2 lg:justify-end">
            <LeaveDayCell
              size="chip"
              color={row.color}
              mark={row.mark}
              status={
                row.status === 'approved' || row.status === 'submitted' ? row.status : 'approved'
              }
              labelDe={row.labelDe}
            />
            <span>
              <span className="font-medium text-foreground">{row.labelDe}</span>
              {statusWord ? <span className="text-muted-foreground"> · {statusWord}</span> : null}
              <span className="text-muted-foreground">
                {' '}
                · {amount}
                {unitLabel ? ` ${unitLabel}` : null}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function LeaveYearMonthList({
  year,
  absences,
  holidays,
  schoolHolidays = [],
  today,
  linkRequests,
  rangeStart = null,
  onPickDate,
  className,
}: {
  year: number;
  absences: readonly LeaveCalendarYearAbsence[];
  holidays: readonly LeaveCalendarHoliday[];
  schoolHolidays?: readonly SchoolHolidaySpan[];
  today: string;
  linkRequests: boolean;
  rangeStart?: string | null;
  onPickDate?: (date: string) => void;
  className?: string;
}) {
  const holidayByDate = useMemo(
    () => new Map(holidays.map((row) => [row.date, row.nameDe])),
    [holidays],
  );
  const byMonth = useMemo(() => {
    const map = new Map<number, LeaveCalendarYearAbsence[]>();
    for (const absence of absences) {
      const month = Number(absence.date.slice(5, 7));
      const list = map.get(month) ?? [];
      list.push(absence);
      map.set(month, list);
    }
    return [...map.entries()].sort((left, right) => left[0] - right[0]);
  }, [absences]);

  if (byMonth.length === 0) {
    const holidayDays = holidays.filter((row) => row.date.startsWith(`${year}-`));
    return (
      <ul className={cn(leavePanelClassName, 'space-y-3 text-sm lg:hidden', className)}>
        {holidayDays.length > 0 ? (
          holidayDays.map((row) => (
            <li key={row.date} className="text-muted-foreground">
              {onPickDate ? (
                <button
                  type="button"
                  className={cn(
                    'text-left',
                    rangeStart === row.date ? 'ring-2 ring-primary ring-offset-1' : null,
                  )}
                  onClick={() => onPickDate(row.date)}
                >
                  <span className="font-medium text-foreground">{formatGermanDate(row.date)}</span>
                  <span> · Feiertag: {row.nameDe}</span>
                </button>
              ) : (
                <>
                  <span className="font-medium text-foreground">{formatGermanDate(row.date)}</span>
                  <span> · Feiertag: {row.nameDe}</span>
                </>
              )}
            </li>
          ))
        ) : (
          <li className="text-muted-foreground">In diesem Jahr keine Abwesenheit.</li>
        )}
      </ul>
    );
  }

  return (
    <div className={cn(leavePanelClassName, 'space-y-4 lg:hidden', className)}>
      {byMonth.map(([month, items]) => (
        <section key={month} aria-labelledby={`leave-year-list-${month}`}>
          <h3 id={`leave-year-list-${month}`} className="mb-2 text-sm font-semibold capitalize">
            {monthLabel(year, month)}
          </h3>
          <ul className="space-y-2 text-sm">
            {items.map((item) => {
              const status =
                item.status === 'approved' || item.status === 'submitted' ? item.status : null;
              const holidayName = holidayByDate.get(item.date);
              const schoolName = schoolHolidayNameOn(item.date, schoolHolidays);
              const row = (
                <span className="flex items-start gap-2">
                  <LeaveDayCell
                    size="chip"
                    color={item.color}
                    mark={item.mark}
                    status={status}
                    part={item.part}
                    holiday={Boolean(holidayName)}
                    holidayName={holidayName}
                    schoolHoliday={schoolName != null}
                    schoolHolidayName={schoolName}
                    today={item.date === today}
                    labelDe={item.labelDe}
                  />
                  <span className="min-w-0 leading-tight">
                    <span className="font-medium">{formatGermanDate(item.date)}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {item.labelDe}
                      {status ? ` · ${LEAVE_REQUEST_STATUS_LABEL[status]}` : ''}
                      {item.part !== 'full'
                        ? item.part === 'morning'
                          ? ' · Vormittag'
                          : ' · Nachmittag'
                        : ''}
                      {holidayName ? ` · ${holidayName}` : ''}
                      {schoolName ? ` · ${schoolName}` : ''}
                    </span>
                  </span>
                </span>
              );
              return (
                <li key={`${item.requestId}-${item.date}`}>
                  {linkRequests ? (
                    <Link
                      to={leaveRequestDetailPath(item.requestId)}
                      className="block rounded-sm hover:bg-muted/60"
                    >
                      {row}
                    </Link>
                  ) : onPickDate ? (
                    <button
                      type="button"
                      className={cn(
                        'block w-full rounded-sm text-left hover:bg-muted/60',
                        rangeStart === item.date ? 'ring-2 ring-primary ring-offset-1' : null,
                      )}
                      onClick={() => onPickDate(item.date)}
                    >
                      {row}
                    </button>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function groupAbsencesByDate(
  absences: readonly LeaveCalendarYearAbsence[],
): Map<string, LeaveCalendarYearAbsence[]> {
  const map = new Map<string, LeaveCalendarYearAbsence[]>();
  for (const absence of absences) {
    const list = map.get(absence.date) ?? [];
    list.push(absence);
    map.set(absence.date, list);
  }
  return map;
}
